/**
 * Impression d'une liste sur papier.
 *
 * Le soir du service, il n'y a ni réseau fiable ni écran à l'accueil : la
 * liste des réservations se pointe au stylo. Imprimer l'écran ne convient pas
 * — la barre de navigation, les boutons d'action et les couleurs de statut y
 * partiraient aussi, et les colonnes utiles se retrouveraient rognées.
 *
 * La liste part donc dans une fenêtre à elle, avec sa propre mise en page :
 * en-tête répété sur chaque feuille, lignes qui ne se coupent pas en deux, et
 * une colonne vide pour cocher les arrivées.
 */

export interface ColonneImpression<T> {
  titre: string;
  /** Contenu de la cellule. Une chaîne vide laisse la case blanche. */
  valeur: (ligne: T) => string;
  alignement?: 'gauche' | 'centre' | 'droite';
}

export interface ListeAImprimer<T> {
  titre: string;
  /** Nom de l'association, en pied de feuille. */
  association?: string;
  /** Événement, date du service, ce qui situe la feuille. */
  sousTitre?: string;
  /** Compteurs rappelés sous le titre : couverts vendus, encaissé… */
  totaux?: { libelle: string; valeur: string }[];
  colonnes: ColonneImpression<T>[];
  lignes: T[];
  /**
   * Intitulé de la colonne à cocher, ajoutée en fin de tableau.
   * Absent, aucune colonne n'est ajoutée.
   */
  colonnePointage?: string;
}

/** Empêche qu'un nom saisi par un convive soit interprété comme du balisage. */
function echapper(valeur: string): string {
  return valeur
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const ALIGNEMENTS = { gauche: 'left', centre: 'center', droite: 'right' } as const;

export function imprimerListe<T>(liste: ListeAImprimer<T>): void {
  const fenetre = window.open('', '_blank');
  if (!fenetre) {
    // Un bloqueur de fenêtres : le dire, plutôt que de laisser croire que le
    // bouton ne fait rien.
    window.alert(
      "L'impression s'ouvre dans une nouvelle fenêtre, que votre navigateur vient de bloquer. " +
        'Autorisez les fenêtres pour ce site, puis réessayez.'
    );
    return;
  }

  const colonnes = [...liste.colonnes];
  if (liste.colonnePointage) {
    colonnes.push({ titre: liste.colonnePointage, valeur: () => '', alignement: 'centre' });
  }

  const enTete = colonnes
    .map(
      (c) =>
        `<th style="text-align:${ALIGNEMENTS[c.alignement ?? 'gauche']}">${echapper(c.titre)}</th>`
    )
    .join('');

  const corps = liste.lignes
    .map(
      (ligne) =>
        '<tr>' +
        colonnes
          .map(
            (c) =>
              `<td style="text-align:${ALIGNEMENTS[c.alignement ?? 'gauche']}">${echapper(
                c.valeur(ligne)
              )}</td>`
          )
          .join('') +
        '</tr>'
    )
    .join('');

  const totaux = (liste.totaux ?? [])
    .map((t) => `<span><strong>${echapper(t.valeur)}</strong> ${echapper(t.libelle)}</span>`)
    .join('');

  const editeLe = new Date().toLocaleString('fr-FR', {
    dateStyle: 'long',
    timeStyle: 'short',
  });

  fenetre.document.write(`<!doctype html>
<html lang="fr">
<head>
<meta charset="utf-8">
<title>${echapper(liste.titre)}</title>
<style>
  @page { size: A4; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, "Segoe UI", sans-serif; color: #111; margin: 0; }
  h1 { font-size: 18pt; margin: 0; }
  .sous-titre { font-size: 11pt; color: #444; margin-top: 2mm; }
  .totaux { margin-top: 4mm; font-size: 10pt; display: flex; flex-wrap: wrap; gap: 6mm; }
  table { width: 100%; border-collapse: collapse; margin-top: 6mm; font-size: 10pt; }
  /* pre-line : une cellule peut porter deux lignes — la référence et le code
     de retrait, une observation en plusieurs phrases. */
  th, td { border: 1px solid #999; padding: 2mm 2.5mm; vertical-align: top; white-space: pre-line; }
  th { background: #f0f0f0; font-weight: 600; }
  /* Une réservation coupée entre deux feuilles se relit deux fois, ou pas du tout. */
  tr { break-inside: avoid; }
  thead { display: table-header-group; }
  .pied { margin-top: 6mm; font-size: 8pt; color: #666; }
  .vide { margin-top: 8mm; font-style: italic; color: #666; }
</style>
</head>
<body>
  <h1>${echapper(liste.titre)}</h1>
  ${liste.sousTitre ? `<p class="sous-titre">${echapper(liste.sousTitre)}</p>` : ''}
  ${totaux ? `<div class="totaux">${totaux}</div>` : ''}
  ${
    liste.lignes.length === 0
      ? '<p class="vide">Aucune réservation à ce jour.</p>'
      : `<table><thead><tr>${enTete}</tr></thead><tbody>${corps}</tbody></table>`
  }
  <p class="pied">Édité le ${echapper(editeLe)}${liste.association ? ' — ' + echapper(liste.association) : ''}</p>
  <!-- Le déclenchement est écrit dans la page elle-même, et non posé depuis
       l'appelant : selon le navigateur, « load » est déjà passé quand la main
       revient de document.close(), et l'impression ne partait jamais. -->
  <script>window.addEventListener('load', function () { window.print(); });</script>
</body>
</html>`);

  fenetre.document.close();
  fenetre.focus();
}
